import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import type { CanonStatus, SourceRef } from "../domain/types";

export function StatusBadge({ status }: { status: string }) {
  return <span className={`badge ${status}`}>{status}</span>;
}

export function Sources({ sources }: { sources?: SourceRef[] }) {
  if (!sources?.length) return <p className="tiny">Aucune source.</p>;
  return (
    <ul className="tiny">
      {sources.map((s, i) => (
        <li key={i}>{s.label}{s.note ? ` — ${s.note}` : ""}</li>
      ))}
    </ul>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
  drawer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  drawer?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-back" onClick={onClose} role="presentation">
      <div
        className={`modal ${drawer ? "drawer" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row space">
          <h2>{title}</h2>
          <button className="btn" type="button" onClick={onClose}>
            Fermer
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function nowIso(): string {
  return new Date().toISOString();
}

export const STATUSES: CanonStatus[] = [
  "canon",
  "draft",
  "verify",
  "legacy",
  "contradiction",
  "rejected",
];

export function useHash(): [string, (path: string) => void] {
  const [hash, setHash] = useState(() => location.hash.replace(/^#/, "") || "/");
  useEffect(() => {
    const on = () => setHash(location.hash.replace(/^#/, "") || "/");
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  const navigate = (path: string) => {
    const next = path.startsWith("/") ? path : `/${path}`;
    if (location.hash.replace(/^#/, "") !== next) location.hash = next;
    setHash(next);
  };
  return [hash, navigate];
}

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <input
      className="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
    />
  );
}

export function SourceFields({
  status,
  sourceLabel,
  setStatus,
  setSourceLabel,
}: {
  status: CanonStatus;
  sourceLabel: string;
  setStatus: (s: CanonStatus) => void;
  setSourceLabel: (s: string) => void;
}) {
  return (
    <>
      <Field label="Statut">
        <select value={status} onChange={(e) => setStatus(e.target.value as CanonStatus)}>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Source (obligatoire pour le canon)">
        <input value={sourceLabel} onChange={(e) => setSourceLabel(e.target.value)} required={status === "canon"} />
      </Field>
    </>
  );
}

export function submitGuard(
  e: FormEvent,
  status: CanonStatus,
  sourceLabel: string,
): boolean {
  e.preventDefault();
  if (status === "canon" && !sourceLabel.trim()) {
    alert("Une source ou une note de décision est obligatoire pour passer en canon.");
    return false;
  }
  return true;
}

export function filterText(hay: string, needle: string): boolean {
  return hay.toLowerCase().includes(needle.trim().toLowerCase());
}

export const LIFE: Array<[string, string]> = [
  ["alive", "Vivant"],
  ["injured", "Blessé"],
  ["unconscious", "Inconscient"],
  ["missing", "Disparu"],
  ["presumed-dead", "Présumé mort"],
  ["dead", "Mort"],
];

